<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import QuranFullPlayer from '@/components/quran/player/QuranFullPlayer.vue';
import QuranMiniPlayer from '@/components/quran/player/QuranMiniPlayer.vue';
import { useQuranPlayer } from '@/composables/quran/useQuranPlayer';
import { useQuranSettings } from '@/composables/quran/useQuranSettings';
import { ayahAnchor, ayahPath, surahPath } from '@/utils/quran/paths';

/**
 * The player on every page: the bar along the bottom while something is
 * queued, the full-screen player it opens into, follow-along reading, and the
 * keyboard (Space play/pause, Shift+→ / Shift+← next / previous ayah).
 */
const route = useRoute();
const router = useRouter();
const player = useQuranPlayer();
const settings = useQuranSettings();
const current = player.current;

const expanded = ref(false);

/**
 * Follow along: on the surah's page, keep the recited ayah in view; on an
 * ayah's page, move to the page of the ayah now being recited. Also fires the
 * moment the setting is switched on, and when the big player closes.
 */
function follow() {
  const item = current.value;
  if (!item || !settings.follow || route.params.surah !== String(item.surah)) return;

  if (route.params.ayah) {
    if (route.params.ayah !== String(item.number)) router.replace(ayahPath(item.surah, item.number));
    return;
  }
  document.getElementById(ayahAnchor(item.number))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

watch(() => current.value?.key, follow);
watch(() => settings.follow, (on) => on && follow());
watch(expanded, (open) => {
  document.documentElement.classList.toggle('overflow-hidden', open);
  if (!open) nextTick(follow);
});

// Room at the bottom of every page for the docked bar.
watch(() => Boolean(current.value), (on) => document.documentElement.classList.toggle('has-player', on), { immediate: true });

function openSurah() {
  const item = current.value;
  expanded.value = false;
  router.push({ path: surahPath(item.surah), hash: `#${ayahAnchor(item.number)}` });
}

function onKey(event) {
  if (!current.value || event.metaKey || event.ctrlKey || event.altKey) return;
  if (event.target.closest('input, textarea, select, [contenteditable="true"], button, a, [role="slider"]')) return;

  if (event.code === 'Space') {
    event.preventDefault();
    player.toggle();
  } else if (event.shiftKey && event.key === 'ArrowRight') {
    event.preventDefault();
    player.next();
  } else if (event.shiftKey && event.key === 'ArrowLeft') {
    event.preventDefault();
    player.previous();
  } else if (event.key === 'Escape' && expanded.value) {
    expanded.value = false;
  }
}
window.addEventListener('keydown', onKey);
onBeforeUnmount(() => window.removeEventListener('keydown', onKey));
</script>

<template>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="translate-y-full"
    leave-active-class="transition duration-200 ease-in"
    leave-to-class="translate-y-full"
  >
    <QuranMiniPlayer v-if="current && !expanded" @expand="expanded = true" />
  </Transition>

  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="translate-y-10 opacity-0"
    leave-active-class="transition duration-200 ease-in"
    leave-to-class="translate-y-10 opacity-0"
  >
    <QuranFullPlayer v-if="current && expanded" @collapse="expanded = false" @open-surah="openSurah" />
  </Transition>
</template>
